import { computed } from 'vue'
import { useState } from '#imports'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import { ONBOARDING_TOURS } from '~/onboarding/catalog'

export type OnboardingTourFlag = {
  id: string
  enabled: boolean
}

type OnboardingToursLoadStatus = 'idle' | 'loading' | 'ready' | 'error'

type OnboardingToursState = {
  status: OnboardingToursLoadStatus
  tours: OnboardingTourFlag[]
  requestSeq: number
  saving: boolean
}

type OnboardingToursResponse = {
  tours?: OnboardingTourFlag[]
}

function normalizeTours(value: unknown): OnboardingTourFlag[] {
  if (!Array.isArray(value)) {
    return []
  }
  const tours: OnboardingTourFlag[] = []
  for (const item of value) {
    if (typeof item !== 'object' || item === null) {
      continue
    }
    const tour = item as Partial<OnboardingTourFlag>
    if (typeof tour.id !== 'string' || typeof tour.enabled !== 'boolean') {
      continue
    }
    tours.push({ id: tour.id, enabled: tour.enabled })
  }
  return tours
}

/**
 * État global d’activation des guides pas à pas (GET /v1/onboarding-tours).
 * Tant que la réponse n’est pas arrivée, le lancement automatique attend.
 * Si le GET échoue, les guides restent actifs.
 */
export function useOnboardingTourSettings() {
  const toast = useSourceToast()
  const state = useState<OnboardingToursState>('onboarding-tours', () => ({
    status: 'idle',
    tours: [],
    requestSeq: 0,
    saving: false,
  }))

  const autoStartAllowed = computed(
    () => state.value.status === 'ready' || state.value.status === 'error'
  )

  const disabledTourIds = computed(() => {
    if (state.value.status !== 'ready') {
      return new Set<string>()
    }
    return new Set(state.value.tours.filter((tour) => !tour.enabled).map((tour) => tour.id))
  })

  const disabledTourSignature = computed(() => {
    return `${state.value.status}:${[...disabledTourIds.value].sort().join('|')}`
  })

  function isTourDisabled(tourId: string): boolean {
    return disabledTourIds.value.has(tourId)
  }

  function reset(): void {
    state.value = {
      status: 'idle',
      tours: [],
      requestSeq: state.value.requestSeq + 1,
      saving: false,
    }
  }

  async function refresh(): Promise<void> {
    const requestSeq = state.value.requestSeq + 1
    const keepCurrentTours = state.value.status === 'ready'
    state.value = {
      status: keepCurrentTours ? 'ready' : 'loading',
      tours: keepCurrentTours ? state.value.tours : [],
      requestSeq,
      saving: state.value.saving,
    }
    try {
      const body = await useApiMonOrga<OnboardingToursResponse>('GET', '/v1/onboarding-tours')
      if (state.value.requestSeq !== requestSeq) {
        return
      }
      state.value = {
        status: 'ready',
        tours: normalizeTours(body.tours),
        requestSeq,
        saving: state.value.saving,
      }
    } catch {
      if (state.value.requestSeq !== requestSeq) {
        return
      }
      state.value = {
        status: 'error',
        tours: [],
        requestSeq,
        saving: state.value.saving,
      }
    }
  }

  async function setTourEnabled(tourId: string, enabled: boolean): Promise<boolean> {
    state.value = { ...state.value, saving: true }
    try {
      await useApiMonOrga('PUT', `/v1/admin/onboarding-tours/${encodeURIComponent(tourId)}`, undefined, {
        enabled,
      })
      await refresh()
      return true
    } catch {
      toast.error('Guide pas à pas', 'La modification n’a pas pu être enregistrée.')
      return false
    } finally {
      state.value = { ...state.value, saving: false }
    }
  }

  async function setAllToursEnabled(enabled: boolean): Promise<void> {
    const tourIds = ONBOARDING_TOURS.map((tour) => tour.id)
    state.value = { ...state.value, saving: true }
    try {
      for (const tourId of tourIds) {
        await useApiMonOrga('PUT', `/v1/admin/onboarding-tours/${encodeURIComponent(tourId)}`, undefined, {
          enabled,
        })
      }
      await refresh()
    } catch {
      toast.error('Guide pas à pas', 'La modification n’a pas pu être enregistrée.')
      await refresh()
    } finally {
      state.value = { ...state.value, saving: false }
    }
  }

  return {
    state,
    autoStartAllowed,
    disabledTourIds,
    disabledTourSignature,
    isTourDisabled,
    refresh,
    reset,
    setTourEnabled,
    setAllToursEnabled,
  }
}
